import { CvcIcon } from '@pm/icons';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { STRIPE_PUBLISHABLE_KEY } from '../../constants';
import { Checkbox } from '../Checkbox';
import { StyledField } from '../StyledField';
import { SubmitButton } from '../SubmitButton';
import { TermsOfService } from '../TermsOfService/TermsOfService';
import {
  CreditCardFormFieldsProps,
  CreditCardFormProps,
  NewCreditCardFormWrapper,
} from './NewCreditCardFormWrapper';

const STRIPE_ELEMENT_STYLES = { style: { base: { fontSize: '16px' } } };

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

export const CreditCardForm: FunctionComponent<CreditCardFormProps> = (
  props,
) => {
  const { showDefaultCheckbox, ...wrapperProps } = props;
  return (
    <Elements stripe={stripePromise}>
      <NewCreditCardFormWrapper
        {...wrapperProps}
        renderForm={(props: CreditCardFormFieldsProps) => (
          <CreditCardFormFields
            {...props}
            showDefaultCheckbox={showDefaultCheckbox}
          />
        )}
      ></NewCreditCardFormWrapper>
    </Elements>
  );
};

const CreditCardFormFields = ({
  handleChange,
  cardErrorMessage,
  cardNumberValid,
  cardExpiryValid,
  cardCvcValid,
  formSubmitted,
  isProvider,
  showDefaultCheckbox,
}: CreditCardFormFieldsProps & { showDefaultCheckbox?: boolean }) => {
  const { t } = useTranslation('payment-methods', {
    keyPrefix: 'Stripe',
  });

  return (
    <div className="space-y-5">
      <div className="space-y-3">
        <div>
          <label className="text-xs font-medium">
            {t('FieldLabels.CardNumber')}
          </label>
          <div className="relative mt-1">
            <CardNumberElement
              options={STRIPE_ELEMENT_STYLES}
              className="rounded-lg bg-white border p-3 leading-6"
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="flex gap-4">
          <div className="flex-1 space-y-1">
            <label className="mb-0 text-xs font-medium">
              {t('FieldLabels.Expiration')}
            </label>
            <CardExpiryElement
              options={STRIPE_ELEMENT_STYLES}
              className="w-full rounded-lg border bg-white p-3 leading-6"
              onChange={handleChange}
            />
          </div>
          <div className="flex-1 space-y-1">
            <label htmlFor="cardCVC" className="mb-0 text-xs font-medium">
              {t('FieldLabels.CVC')}
            </label>

            <div className="relative flex">
              <CardCvcElement
                options={STRIPE_ELEMENT_STYLES}
                className="w-full rounded-lg border bg-white p-3 leading-6"
                onChange={handleChange}
              />
              <CvcIcon className="absolute right-3 self-center" />
            </div>
          </div>
        </div>

        {cardErrorMessage && (
          <p className="text-critical-dark mb-2 text-sm">{cardErrorMessage}</p>
        )}

        <div className="space-y-1">
          <label htmlFor="postalCode" className="text-xs font-medium">
            {t('FieldLabels.PostalCode')}
          </label>
          <StyledField
            id="postalCode"
            name="postalCode"
            borderVariant="light"
            className="p-3 leading-6 text-base"
            inputStyle=""
            placeholder={t('FieldLabels.PostalCode')}
          />
        </div>
      </div>

      {showDefaultCheckbox && (
        <Checkbox name="isDefault" className="ml-3">
          {t('FieldLabels.SetAsDefault')}
        </Checkbox>
      )}

      <SubmitButton
        disabled={
          !cardNumberValid || !cardExpiryValid || !cardCvcValid || formSubmitted
        }
      >
        {isProvider
          ? t('FieldLabels.ProviderSubmitButton')
          : t('FieldLabels.Save')}
      </SubmitButton>

      {!isProvider && (
        <TermsOfService
          link="https://talkwithfrida.com/terms-of-use"
          service="Stripe"
        />
      )}
    </div>
  );
};
